import { compact } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EngagementSurveyReportGroupBy,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Checkbox from "@components/checkbox";
import { Select, SelectOption } from "@components/select/select";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

import ViewOptionsSidebar from "../sidebar/view-options-sidebar";
import EngagementSurveyReportFilterContainer from "./engagement-survey-report-filter-container";
import { getResponderKey, getResponderLabel } from "./helpers";
import { EngagementSurveyReportViewOptions } from "./types";

const EngagementSurveyReportViewOptionsSidebar = ({
  viewOptions,
  onUpdateViewOptions,
  onClose,
  responders,
}: {
  viewOptions: EngagementSurveyReportViewOptions;
  onUpdateViewOptions: (
    opts: Partial<EngagementSurveyReportViewOptions>,
  ) => void;
  onClose: () => void;
  responders: EngagementSurveyReportResponderFragment[];
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const debouncedId = useRef<NodeJS.Timeout | null>(null);
  const [proposedHiddenColumns, setProposedHiddenColumns] = useState(
    viewOptions.hiddenColumns,
  );

  useEffect(() => {
    setProposedHiddenColumns(viewOptions.hiddenColumns);
  }, [viewOptions.hiddenColumns]);

  useEffect(() => {
    if (debouncedId.current) {
      const timeoutId = debouncedId.current;
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, []);

  const groupByOptions = useMemo(
    () =>
      compact([
        {
          label: label("team", { capitalize: true }),
          value: EngagementSurveyReportGroupBy.Team,
        },
        {
          label: "Reports To (direct reports)",
          value: EngagementSurveyReportGroupBy.Manager,
        },
        {
          label: "Reports To (full reporting tree)",
          value: EngagementSurveyReportGroupBy.ManagerFullRollup,
        },
        {
          label: "ICs vs Managers",
          value: EngagementSurveyReportGroupBy.IsManager,
        },
        organization.hasCareerFramework && {
          label: "Career track",
          value: EngagementSurveyReportGroupBy.CareerTrack,
        },
        {
          label: "Location",
          value: EngagementSurveyReportGroupBy.Location,
        },
      ]),
    [label, organization],
  );

  const columnResponders = useMemo(
    () =>
      responders.filter(
        (r) =>
          ![
            EngagementSurveyRollupType.Organization,
            EngagementSurveyRollupType.Collapsed,
          ].includes(r.rollupType),
      ),
    [responders],
  );

  const handleChangeGroupBy = useCallback(
    (opt: SelectOption<EngagementSurveyReportGroupBy>) => {
      onUpdateViewOptions({
        groupBy: opt.value,
        hiddenColumns: [],
      });
    },
    [onUpdateViewOptions],
  );

  const handleToggleColumn = useCallback(
    (responderKey: string) => {
      const isVisible = !proposedHiddenColumns.includes(responderKey);
      const newColumns = isVisible
        ? [...proposedHiddenColumns, responderKey]
        : proposedHiddenColumns.filter((key) => key !== responderKey);
      setProposedHiddenColumns(newColumns);

      const oldTimeout = debouncedId.current;
      if (oldTimeout) {
        clearTimeout(oldTimeout);
      }
      debouncedId.current = setTimeout(() => {
        onUpdateViewOptions({ hiddenColumns: newColumns });
      }, 800);
    },
    [proposedHiddenColumns, onUpdateViewOptions],
  );

  const handleSelectAll = useCallback(() => {
    onUpdateViewOptions({ hiddenColumns: [] });
  }, [onUpdateViewOptions]);

  const handleDeselectAll = useCallback(() => {
    const allResponderKeys = columnResponders.map((r) => getResponderKey(r));
    onUpdateViewOptions({ hiddenColumns: allResponderKeys });
  }, [columnResponders, onUpdateViewOptions]);

  const showCheckboxes =
    viewOptions.groupBy !== EngagementSurveyReportGroupBy.IsManager &&
    columnResponders.length > 2;

  return (
    <ViewOptionsSidebar onClose={onClose}>
      <div className="border-t p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Filters</div>
        </div>
        <div>
          <EngagementSurveyReportFilterContainer
            filters={viewOptions.filters.list}
            onUpdateFilters={(newFilters) =>
              onUpdateViewOptions({
                filters: { ...viewOptions.filters, list: newFilters },
              })
            }
          />
        </div>
      </div>

      <div className="border-t p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Group By (Columns)</div>
          {showCheckboxes && (
            <div className="flex gap-2">
              <Button
                text="Select All"
                theme={buttonTheme.lightBlue}
                className="bg-transparent font-medium"
                onClick={handleSelectAll}
              />
              <Button
                text="Deselect All"
                theme={buttonTheme.lightBlue}
                className="bg-transparent font-medium"
                onClick={handleDeselectAll}
              />
            </div>
          )}
        </div>
        <div className="mb-6">
          <Select
            prefix="Group type"
            options={groupByOptions}
            value={viewOptions.groupBy}
            onChange={handleChangeGroupBy}
          />
        </div>
        {showCheckboxes && (
          <>
            <div className="mb-6 p-4 border rounded-lg flex gap-3 bg-gray-50 border-gray-100 text-gray-600 text-sm">
              <TopicflowIcon type="info" className="mt-0.5 h-4 w-4" />
              <div>
                Columns that are not selected will be automatically grouped into
                one shared column.
              </div>
            </div>
            <div className="flex flex-col gap-2 text-gray-600">
              {columnResponders.map((responder) => {
                const responderKey = getResponderKey(responder);
                const isChecked = !proposedHiddenColumns.includes(responderKey);

                return (
                  <Checkbox
                    key={responderKey}
                    label={getResponderLabel(responder)}
                    checked={isChecked}
                    onChange={() => handleToggleColumn(responderKey)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </ViewOptionsSidebar>
  );
};

export default EngagementSurveyReportViewOptionsSidebar;
